import type { SourceTarget } from './brief-source-target';

/*
 * The brief's UNIVERSAL CLICK CONTRACT (ruling C1, walk 2026-08-02).
 *
 * Before this, clicking a brief card was a slot machine: four exit vocabularies coexisted
 * ([Open the note][Source][Notes][Ask] / [Open in source ↗][Open the note] / [Open in source ↗]
 * alone / [Open source A][Open source B][Full record on Actions]), the stats board had no in-app
 * destination at all, and the priority column carried three drill-in depths — one of which was a
 * card with NO working verb (bug-614: a focus task with `conversationId = null` and
 * `externalUrl = null` fell out the bottom of an open-handler ladder and rendered zero interactive
 * affordances). No habit can form against that.
 *
 * One contract, derived in one place:
 *
 *   [Open the note]     in-app → #/conversations/:id    when a conversationId exists
 *   [Open in source ↗]  external, new tab               when a real artifact url exists
 *   [Ask]               → /ask?q=<prefill>              ALWAYS
 *
 * [Ask] is what makes bug-614 structurally unreachable rather than guarded: it needs no ids, so the
 * exit list is never empty and a card can no longer be a dead end no matter what the generator
 * emits. The render-side guard the bug log asked for falls out of the contract instead of being a
 * predicate someone has to remember.
 *
 * Ask NEVER auto-submits — /ask?q= prefills and focuses the composer (AskLauncher's own contract; a
 * 20-45s turn must not fire from a URL).
 *
 * BUTTON WORDING IS RYAN-GATED. These three strings are the decision ledger's defaults, shipped
 * pending his call; they live here so a reword is one edit, not a hunt through five components.
 */

export const EXIT_LABEL = {
  note: 'Open the note',
  source: 'Open in source ↗',
  ask: 'Ask',
} as const;

export type BriefExitKind = keyof typeof EXIT_LABEL;

/** One external place a card's material actually appeared. Only rendered in the source picker. */
export interface BriefSourceExitTarget {
  label: string;
  site: string | null;
  href: string;
}

export interface BriefExit {
  kind: BriefExitKind;
  label: string;
  /** `note` only — the conversation to open in-app. */
  conversationId?: string;
  /** `source` only — never empty when the exit is present; >1 renders the picker. */
  targets?: BriefSourceExitTarget[];
  /** `ask` only — the composer prefill. */
  prefill?: string;
}

export interface BriefCardExitInput {
  /** The card's own note. Nullish on cards the generator could not ground (bug-614's shape). */
  conversationId?: string | null;
  /** A single external artifact, for cards that carry a url but no citation list (task cards). */
  externalUrl?: string | null;
  /** Every place the item appeared, from resolveSourceTargets. Url-less entries are NOTE targets
   *  (a teammate's Gmail thread is in THEIR mailbox — bug-538) and are deliberately not sources. */
  sourceTargets?: SourceTarget[] | null;
  /** Card title — the Ask prefill is composed from it when askPrefill is not supplied. */
  title?: string | null;
  askPrefill?: string | null;
}

function trimmed(value: string | null | undefined): string | null {
  const v = (value ?? '').trim();
  return v ? v : null;
}

/** Hostname of an artifact url, for the picker row of a card whose url has no citation label. */
function hostLabel(href: string): string {
  try {
    return new URL(href).hostname.replace(/^www\./, '') || 'The source';
  } catch {
    return 'The source';
  }
}

export function askPrefillFor(title: string | null | undefined): string {
  const t = trimmed(title);
  return t ? `What should I know about ${t}?` : 'What should I know about this?';
}

/** The nav id an `ask` exit hands to onNav. Kept here so every caller composes it identically. */
export function askNavTarget(prefill: string): string {
  return `ask?q=${encodeURIComponent(prefill)}`;
}

/** The source exit's tile label. The count sits BEFORE the arrow so the glyph stays trailing. */
export function sourceExitLabel(count: number): string {
  return count > 1 ? `Open in source (${count}) ↗` : EXIT_LABEL.source;
}

/**
 * The exits a brief card offers, in contract order. Never returns an empty list.
 *
 * The `source` exit is EXTERNAL-ONLY by design. resolveSourceTargets deliberately falls back to our
 * own compaction so that [Source] could never be dead under the older D4 rule; under C1 that
 * fallback would render a second button doing exactly what [Open the note] does, so url-less
 * targets are dropped here rather than in the component.
 */
export function briefCardExits(input: BriefCardExitInput): BriefExit[] {
  const exits: BriefExit[] = [];

  const conversationId = trimmed(input.conversationId);
  if (conversationId) {
    exits.push({ kind: 'note', label: EXIT_LABEL.note, conversationId });
  }

  const targets: BriefSourceExitTarget[] = [];
  const seen = new Set<string>();
  for (const t of input.sourceTargets ?? []) {
    const href = trimmed(t?.href);
    if (!href || seen.has(href)) continue;
    seen.add(href);
    targets.push({ label: trimmed(t.label) ?? hostLabel(href), site: t.site ?? null, href });
  }
  const externalUrl = trimmed(input.externalUrl);
  if (externalUrl && !seen.has(externalUrl)) {
    seen.add(externalUrl);
    targets.push({ label: hostLabel(externalUrl), site: null, href: externalUrl });
  }
  if (targets.length > 0) {
    exits.push({ kind: 'source', label: EXIT_LABEL.source, targets });
  }

  exits.push({
    kind: 'ask',
    label: EXIT_LABEL.ask,
    prefill: trimmed(input.askPrefill) ?? askPrefillFor(input.title),
  });

  return exits;
}

/*
 * ─── The RULED dialog's open control (popout build spec §5.5 / ruling 9, round-6 mock) ───
 *
 * The brief-object cards' stored-body layout replaces the exit-tile grid with ONE real button
 * bottom-right:
 *
 *   `Open the source ▾`  when external targets exist — hover/focus expands the picker, one
 *                        destination per row (the source url when present, else the note), and
 *                        the list ALWAYS ends with a "The note" row for the card's own note
 *                        when it has one (audit finding: a fused card whose citations all carry
 *                        urls would otherwise lose its in-app note exit — LOCK C1's "same
 *                        exits" must hold).
 *   `Open the note`      when only the note exists — direct, no picker.
 *   nothing              when the card has neither a conversationId nor an external target
 *                        (bug-614's ungrounded shapes) — Ask + Done remain in the footer.
 *
 * Per-member visibility ("shared with you" / "private to you") renders INSIDE picker rows —
 * one card-level line cannot represent a multi-member object with mixed visibility (§5.3).
 *
 * WORDING IS RYAN-GATED (spec §7): ruling 9 changes the exit SET — the note exit survives only
 * via the picker's guaranteed row, one hover deeper than today on cards with external sources.
 * These strings ship pending his explicit nod; the legacy EXIT_LABEL block above keeps the
 * frozen v1 dialog's wording untouched until PR C.
 */

export const RULED_EXIT_LABEL = {
  source: 'Open the source',
  note: 'Open the note',
  noteRow: 'The note',
  noteRowMeta: 'no external source · opens the note',
  ask: 'Ask',
} as const;

/** One picker row: exactly one destination — external (href) or the note (conversationId). */
export interface BriefPickerRow {
  label: string;
  /** The quiet second line: site/repo · visibility · destination hint. Null renders nothing. */
  meta: string | null;
  href: string | null;
  conversationId: string | null;
}

export type BriefObjectOpenControl =
  | { kind: 'source'; label: string; rows: BriefPickerRow[] }
  | { kind: 'note'; label: string; conversationId: string };

const VISIBILITY_ROW_LABEL: Record<string, string> = {
  audience_shared: 'shared with you',
  own_private: 'private to you',
};

function pickerMeta(parts: Array<string | null | undefined>): string | null {
  const joined = parts.filter(Boolean).join(' · ');
  return joined || null;
}

/**
 * Derive the ruled open control from the card's resolved targets (resolveSourceTargets — the
 * resolution stays there; this only arranges rows) + its own note. Null = the button is hidden.
 */
export function briefObjectOpenControl(input: {
  targets: Array<{
    label: string;
    site: string | null;
    href: string | null;
    conversationId: string | null;
    visibility?: string | null;
  }>;
  conversationId: string | null;
  /** A single external artifact for cards with a url but no citation list (priority tasks). */
  externalUrl?: string | null;
  /** Human site label, resolved by the caller (lib/sites.ts stays the one vocabulary). */
  siteLabelOf: (site: string) => string;
}): BriefObjectOpenControl | null {
  const conversationId = trimmed(input.conversationId);
  const externalUrl = trimmed(input.externalUrl);
  const targets = [...input.targets];
  if (externalUrl && !targets.some(t => trimmed(t.href) === externalUrl)) {
    targets.push({ label: hostLabel(externalUrl), site: null, href: externalUrl, conversationId: null });
  }
  const externals = targets.filter(t => trimmed(t.href));

  if (externals.length === 0) {
    return conversationId ? { kind: 'note', label: RULED_EXIT_LABEL.note, conversationId } : null;
  }

  const rows: BriefPickerRow[] = [];
  let ownNoteRow: BriefPickerRow | null = null;
  for (const t of targets) {
    const href = trimmed(t.href);
    const visibility = t.visibility ? (VISIBILITY_ROW_LABEL[t.visibility] ?? null) : null;
    if (href) {
      rows.push({
        label: t.label,
        meta: pickerMeta([t.site ? `${input.siteLabelOf(t.site)} ↗` : '↗', visibility]),
        href,
        conversationId: null,
      });
    } else if (t.conversationId) {
      // A url-less member target opens ITS note (rulings 7-8: one destination per row).
      const row: BriefPickerRow = {
        label: t.label,
        meta: pickerMeta([RULED_EXIT_LABEL.noteRowMeta, visibility]),
        href: null,
        conversationId: t.conversationId,
      };
      // The card's own note is held out and appended LAST (spec §5.5: "the list ALWAYS ends
      // with" the in-app note exit), keeping its member-row label when it arrived as one.
      if (t.conversationId === conversationId && !ownNoteRow) ownNoteRow = row;
      else rows.push(row);
    }
  }
  if (conversationId) {
    rows.push(
      ownNoteRow ?? { label: RULED_EXIT_LABEL.noteRow, meta: 'opens the note', href: null, conversationId },
    );
  }

  return { kind: 'source', label: RULED_EXIT_LABEL.source, rows };
}
